/**
 * useRouterRead — the single-read hook behind every Models & Routing panel.
 *
 * The 19 Oct crash: the old Models & Providers panel fired ~23 engine
 * requests at once (one spend-limit and one data-terms read per provider,
 * plus health and prices), which took every engine database connection and
 * slowed sign-in past a minute. This hook is the section's defence:
 *
 *  - One request per read. Panels declare the read they need; SWR dedupes
 *    by key, so two panels needing the same read share one wire request.
 *    Nothing here can fan out per provider row.
 *  - Every request has a time limit. A read that outlives its budget fails
 *    with a plain RouterReadTimeoutError instead of hanging the panel.
 *  - No automatic retry storms. Failures surface in the panel with a manual
 *    retry; SWR's automatic error retry and focus revalidation are off.
 */

import useSWR from 'swr';

/** Default per-read budget. Well under the 60–140 s the exhausted pool
 *  produced on 19 Oct; long enough for a healthy tenant-scoped read. */
export const ROUTER_READ_TIMEOUT_MS = 10_000;

/** Dedupe window for identical keys across panels mounting together. */
export const ROUTER_READ_DEDUPE_MS = 5_000;

export class RouterReadTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`The engine took too long to answer (over ${Math.round(timeoutMs / 1000)}s).`);
    this.name = 'RouterReadTimeoutError';
  }
}

export interface RouterReadOptions {
  timeoutMs?: number;
}

export interface RouterReadResult<T> {
  data: T | undefined;
  error: Error | undefined;
  isLoading: boolean;
  /** Re-fires this read only. */
  retry: () => void;
}

/**
 * Race the fetcher against the time budget. On timeout the abort signal
 * fires as well, so fetchers that accept a signal can cancel the wire
 * request instead of letting it settle unseen.
 */
function raced<T>(fetcher: (signal: AbortSignal) => Promise<T>, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const budget = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      if (!settled) {
        controller.abort();
        reject(new RouterReadTimeoutError(timeoutMs));
      }
    }, timeoutMs);
  });
  return Promise.race([fetcher(controller.signal), budget]).finally(() => {
    settled = true;
    if (timer !== undefined) clearTimeout(timer);
  });
}

export function useRouterRead<T>(
  key: string | null,
  fetcher: (signal: AbortSignal) => Promise<T>,
  options?: RouterReadOptions,
): RouterReadResult<T> {
  const timeoutMs = options?.timeoutMs ?? ROUTER_READ_TIMEOUT_MS;
  const { data, error, isLoading, mutate } = useSWR<T, Error>(
    key,
    key == null ? null : () => raced(fetcher, timeoutMs),
    {
      dedupingInterval: ROUTER_READ_DEDUPE_MS,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );
  return {
    data,
    error,
    isLoading: key != null && isLoading,
    retry: () => {
      void mutate();
    },
  };
}
